'use client';

import React, { Suspense } from 'react';
import Link from 'next/link';
import {
  Crown,
  Heart,
  Users,
  MessageCircle,
  Rss,
  Zap,
  ShieldCheck,
  UserCircle,
  EyeOff,
  Bell,
  Filter,
  BadgeCheck,
  Headphones,
  ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { GlassButton } from '@/components/ui/glass-button';
import { Navbar } from '@/components/layout/Navbar';
import { ToastContainer } from '@/components/ui/Toast';

const FEATURES = [
  {
    icon: Filter,
    title: 'Gender Filter',
    description:
      'Choose to connect exclusively with female or male users. Skip the randomness when you want a specific experience.',
  },
  {
    icon: Heart,
    title: 'Meet Girls Mode',
    description:
      'One-tap access to a dedicated female-only matching queue. Priority placement so you wait less.',
  },
  {
    icon: Users,
    title: 'Friends List',
    description:
      'Add strangers you vibe with as friends. Build your social circle and never lose a great connection again.',
  },
  {
    icon: MessageCircle,
    title: 'Private Messaging',
    description:
      'Start private, persistent conversations with friends. Your messages are saved and synced across devices.',
  },
  {
    icon: Rss,
    title: 'Feed Posting',
    description:
      'Share thoughts, photos, and moments on the public feed. Express yourself beyond the chat window.',
  },
  {
    icon: UserCircle,
    title: 'Persistent Profile',
    description:
      'Set a username, profile photo, and bio. Stand out and let people know who they\'re talking to.',
  },
  {
    icon: Zap,
    title: 'Priority Matching',
    description:
      'Jump to the front of the queue. Get matched faster than free users — no more waiting.',
  },
  {
    icon: EyeOff,
    title: 'Ad-Free Experience',
    description:
      'Zero ads, zero distractions. Enjoy a clean, immersive chat experience with no interruptions.',
  },
  {
    icon: BadgeCheck,
    title: 'Verified Badge',
    description:
      'A visible badge on your profile and messages showing you\'re a trusted, verified Premium member.',
  },
  {
    icon: Bell,
    title: 'Smart Notifications',
    description:
      'Get notified when friends come online, when someone replies to your feed post, or when a match is found.',
  },
  {
    icon: ShieldCheck,
    title: 'Advanced Safety Tools',
    description:
      'Access enhanced moderation options, hidden mode, and the ability to report with priority review.',
  },
  {
    icon: Headphones,
    title: 'Premium Support',
    description:
      'Direct access to our support team with faster response times. Your issues are handled first.',
  },
];

function PremiumPage() {
  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-brand-black pt-20 pb-20 md:pb-0 overflow-x-hidden">
        {/* Background glow */}
        <div className="pointer-events-none absolute inset-0">
          <div
            className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] opacity-10"
            style={{
              background: 'radial-gradient(ellipse, #C4D600 0%, transparent 70%)',
              filter: 'blur(80px)',
            }}
          />
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-16">
          {/* Hero */}
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-brand-lime/20 bg-brand-lime/5 text-xs font-medium text-brand-lime mb-6">
              <Crown size={12} />
              Stranger World Premium
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-brand-white mb-5 leading-tight">
              Everything you need for<br />
              <span className="gradient-lime-text text-glow-lime">better connections</span>
            </h1>
            <p className="text-brand-gray-light max-w-xl mx-auto text-lg leading-relaxed mb-8">
              Premium unlocks the full Stranger World experience — smarter matching, real friendships, and a voice on the feed.
            </p>

            {/* Coming soon pill */}
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full border border-brand-lime/30 bg-brand-lime/10">
              <span className="w-2 h-2 rounded-full bg-brand-lime animate-pulse" />
              <span className="text-brand-lime font-semibold text-sm tracking-wide">
                Pricing coming soon — all features free while we get ready
              </span>
            </div>
          </div>

          {/* Features grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-20">
            {FEATURES.map(({ icon: Icon, title, description }) => (
              <div
                key={title}
                className="group bg-surface-elevated rounded-2xl p-6 border border-white/[0.06] hover:border-brand-lime/20 transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-xl bg-brand-lime/10 flex items-center justify-center mb-4 group-hover:bg-brand-lime/20 transition-colors">
                  <Icon size={20} className="text-brand-lime" />
                </div>
                <h3 className="text-base font-semibold text-brand-white mb-2">{title}</h3>
                <p className="text-sm text-brand-gray leading-relaxed">{description}</p>
              </div>
            ))}
          </div>

          {/* Bottom CTA banner */}
          <div className="rounded-2xl border border-brand-lime/20 bg-brand-lime/[0.04] p-8 sm:p-12 text-center">
            <Crown size={36} className="text-brand-lime mx-auto mb-4" />
            <h2 className="text-2xl sm:text-3xl font-bold text-brand-white mb-3">
              Pricing is on the way
            </h2>
            <p className="text-brand-gray max-w-md mx-auto mb-8 text-sm">
              We&apos;re putting the finishing touches on our subscription plans. Until then, every Premium feature is open to everyone — no credit card needed.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link href="/chat">
                <GlassButton size="lg" contentClassName="flex items-center gap-2">
                  <MessageCircle size={16} />
                  Start Chatting
                  <ArrowRight size={14} />
                </GlassButton>
              </Link>
              <Link href="/">
                <GlassButton size="lg">
                  Back to Home
                </GlassButton>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
}

export default function PremiumPageWrapper() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-brand-black" />}>
      <PremiumPage />
    </Suspense>
  );
}
